// src/components/OSINT/AccountsBrowser.js
//
// OSINT > Leaks > Accounts. A searchable, paginated, severity-sorted list of
// every account that has IntelX findings. Each row opens that account's leak
// provenance trace. This is the way to navigate findings without hunting
// through a single search's (often hundreds of rows) result list.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader,
  Search as SearchIcon,
  Users,
} from 'lucide-react';
import { osintAPI } from '../../services/api';
import PageHeader from '../common/PageHeader';

const SEV = {
  critical:  { label: 'Critical',  dot: '#ef4444', chip: 'text-red-700 bg-red-100 border-red-300' },
  suspected: { label: 'Suspected', dot: '#f43f5e', chip: 'text-rose-700 bg-rose-100 border-rose-300' },
  high:      { label: 'High',      dot: '#f97316', chip: 'text-orange-700 bg-orange-100 border-orange-300' },
  medium:    { label: 'Medium',    dot: '#eab308', chip: 'text-yellow-700 bg-yellow-100 border-yellow-300' },
  low:       { label: 'Low',       dot: '#3b82f6', chip: 'text-blue-700 bg-blue-100 border-blue-300' },
  info:      { label: 'Info',      dot: '#94a3b8', chip: 'text-slate-700 bg-slate-100 border-slate-300' },
  noise:     { label: 'Noise',     dot: '#475569', chip: 'text-slate-500 bg-slate-100 border-slate-200' },
};
const SEV_ORDER = ['critical', 'suspected', 'high', 'medium', 'low', 'info', 'noise'];
const PAGE_SIZE = 25;

const fmtRelative = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};

const AccountsBrowser = () => {
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (qArg, pageArg) => {
    setLoading(true);
    setError(null);
    try {
      const r = await osintAPI.listAccounts({
        q: qArg || undefined,
        limit: PAGE_SIZE,
        offset: pageArg * PAGE_SIZE,
      });
      setAccounts(r.data.accounts || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce the search box; resetting to page 0 on a new query.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    load(debouncedQ, page);
  }, [debouncedQ, page, load]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div>
      <PageHeader
        title="Accounts"
        subtitle="Every account with IntelX findings · worst severity first"
        icon={Users}
        showBackButton
      />

      <div className="px-6 py-6 space-y-5">
        {/* Search */}
        <div className="relative max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter accounts (e.g. jshields, @wasabi.com)..."
            className="terminal-input w-full pl-9 pr-3 py-2.5 rounded-lg text-sm"
            autoFocus
          />
        </div>

        {error && (
          <div className="cyber-card rounded-lg p-4 border border-danger-red/40 bg-danger-red/5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-danger-red flex-shrink-0 mt-0.5" />
            <p className="text-danger-red text-sm">{error}</p>
          </div>
        )}

        {/* Count + pagination header */}
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            {loading ? 'Loading...' : total === 0 ? 'No accounts' : `${rangeStart}-${rangeEnd} of ${total} accounts`}
          </span>
          {pageCount > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="flex items-center gap-1 px-2 py-1 rounded border border-cyber-300/40 text-slate-500 hover:border-cyber-300 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Prev
              </button>
              <span className="text-slate-500">
                {page + 1} / {pageCount}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1 || loading}
                className="flex items-center gap-1 px-2 py-1 rounded border border-cyber-300/40 text-slate-500 hover:border-cyber-300 hover:text-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-16">
            <Loader className="w-4 h-4 animate-spin" />
            <span>Loading accounts...</span>
          </div>
        ) : accounts.length === 0 ? (
          <div className="rounded-2xl border border-cyber-300/40 bg-cyber-100/30 p-10 text-center">
            <Users className="w-10 h-10 text-shinobi-green/50 mx-auto mb-3" />
            <p className="text-slate-700 text-sm">
              {debouncedQ ? `No accounts match "${debouncedQ}".` : 'No accounts with findings yet.'}
            </p>
            <p className="text-slate-500 text-xs mt-1">
              Run a search in OSINT &gt; Leaks to populate findings.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden divide-y divide-cyber-300/20">
            {accounts.map((a) => {
              const worst = SEV[a.worst_severity] || SEV.info;
              return (
                <button
                  key={a.account}
                  onClick={() => navigate(`/osint/leaks/accounts/${encodeURIComponent(a.account)}`)}
                  className="w-full text-left px-5 py-3.5 flex items-center gap-4 hover:bg-cyber-200/30 transition-colors group"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: worst.dot, boxShadow: 'none' }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-mono text-slate-900 group-hover:text-shinobi-green transition-colors break-all">
                        {a.account}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-cyber-300/40 text-slate-500">
                        {a.account_kind}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {a.finding_count} finding{a.finding_count === 1 ? '' : 's'} · last seen {fmtRelative(a.last_seen_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end max-w-[45%]">
                    {SEV_ORDER.map((s) => {
                      const n = a.severity_counts?.[s] || 0;
                      if (n === 0) return null;
                      return (
                        <span
                          key={s}
                          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${SEV[s].chip}`}
                        >
                          {n} {SEV[s].label}
                        </span>
                      );
                    })}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-shinobi-green transition-colors flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountsBrowser;
